import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Edit, Trash2, Search, AlertTriangle, ClipboardCheck, Calendar } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BusinessReviewSheet } from '@/components/admin/BusinessReviewSheet';
import { BusinessEditSheet } from '@/components/admin/BusinessEditSheet';
import { useCategories } from '@/hooks/useCategories';
import { getBusinessStatus } from '@/utils/businessUtils';
import BusinessSubscriptionBadgeStatic from '@/components/admin/BusinessSubscriptionBadgeStatic';
import { AdminPagination } from '@/components/admin/AdminPagination';

interface Business {
  id: string;
  name: string;
  contact_email: string;
  contact_phone: string;
  city: string | null;
  isfeatured: boolean;
  is_active: boolean;
  is_listed: boolean;
  created_at: string;
  category_id: string;
  subscription?: {
    status: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
    plan_name: string | null;
  } | null;
}

const BusinessManagement = () => {
  const { user } = useAuth();
  const { currentCommunity } = useCommunity();
  const { data: categoriesData } = useCategories();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse URL parameters first
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab');
  const reviewIdFromUrl = queryParams.get('review');
  const editIdFromUrl = queryParams.get('edit');
  
  // Initialize state with URL parameters
  const [activeTab, setActiveTab] = useState(tabFromUrl || "all");
  const [reviewBusinessId, setReviewBusinessId] = useState<string | null>(reviewIdFromUrl);
  const [editBusinessId, setEditBusinessId] = useState<string | null>(editIdFromUrl);
  
  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    
    // Handle tab parameter
    if (activeTab === "all") {
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', activeTab);
    }
    
    // Handle review parameter
    if (reviewBusinessId) {
      newSearchParams.set('review', reviewBusinessId);
    } else {
      newSearchParams.delete('review');
    }
    
    // Handle edit parameter
    if (editBusinessId) {
      newSearchParams.set('edit', editBusinessId);
    } else {
      newSearchParams.delete('edit');
    }
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString()
    }, { replace: true });
  }, [activeTab, reviewBusinessId, editBusinessId, location.pathname, location.search, navigate]);
  

  const { data: businessesResponse, isLoading, error } = useQuery({
    queryKey: ['admin', 'businesses', currentCommunity.id, activeTab, searchTerm, currentPage, pageSize],
    queryFn: async () => {
      let query = supabase
        .from('businesses')
        .select(`
          id, 
          name, 
          contact_email, 
          contact_phone,
          city,
          isfeatured,
          is_active,
          is_listed,
          created_at,
          category_id,
          business_subscriptions!left(
            status,
            current_period_end,
            cancel_at_period_end,
            plan_id,
            event_timestamp,
            community_subscription_plans(name)
          )
        `, { count: 'exact' })
        .eq('community_id', currentCommunity.id);

      // Apply status filters
      if (activeTab === "pending") {
        query = query.eq('is_active', false);
      } else if (activeTab === "listed") {
        query = query.eq('is_listed', true);
      } else if (activeTab === "pending-subscription") {
        query = query.eq('is_active', true).eq('is_listed', false);
      }

      // Apply search filter
      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,contact_email.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
      }

      // Apply pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;
      
      query = query
        .order('created_at', { ascending: false })
        .range(from, to);
        
      const { data, error, count } = await query;
      if (error) throw error;
      
      // Process the data to include subscription information
      const processedData = (data || []).map(business => {
        // Sort subscriptions by event_timestamp descending to get the latest one
        const sortedSubscriptions = business.business_subscriptions?.sort((a, b) => 
          new Date(b.event_timestamp).getTime() - new Date(a.event_timestamp).getTime()
        );
        const latestSubscription = sortedSubscriptions?.[0];
        
        let subscription = null;
        if (latestSubscription) {
          subscription = {
            status: latestSubscription.status,
            current_period_end: latestSubscription.current_period_end,
            cancel_at_period_end: latestSubscription.cancel_at_period_end,
            plan_name: latestSubscription.community_subscription_plans?.name || null
          };
        }
        
        // Remove the business_subscriptions array and add the processed subscription
        const { business_subscriptions, ...businessData } = business;
        return {
          ...businessData,
          subscription
        };
      });
      
      return {
        businesses: processedData,
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    },
    enabled: !!user && !!currentCommunity.id
  });

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, pageSize]);

  // Function to get category name from category_id
  const getCategoryName = (categoryId: string): string => {
    if (!categoriesData?.categories) return 'Uncategorized';
    const category = categoriesData.categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const deleteBusinessMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('businesses')
        .delete()
        .eq('id', id)
        .eq('community_id', currentCommunity.id);
        
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      toast({
        title: "Business deleted",
        description: "The business has been successfully deleted",
        variant: "default",
      });
      
      queryClient.invalidateQueries({ queryKey: ['admin', 'businesses', currentCommunity.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'businesses-counts', currentCommunity.id] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete business",
        variant: "destructive",
      });
    }
  });

  
  const handleReviewBusiness = (id: string) => {
    setReviewBusinessId(id);
  };

  const handleEditBusiness = (id: string) => {
    setEditBusinessId(id);
  };

  const handleDeleteBusiness = (id: string) => {
    if (window.confirm("Are you sure you want to delete this business? This action cannot be undone.")) {
      deleteBusinessMutation.mutate(id);
    }
  };

  // Enhanced delete handler that checks subscription status
  const handleDeleteBusinessWithCheck = (businessId: string, hasActiveSubscription?: boolean) => {
    if (hasActiveSubscription) {
      // This shouldn't happen due to UI restrictions, but just in case
      toast({
        title: "Cannot Delete Business",
        description: "This business has an active subscription. Please cancel the subscription first.",
        variant: "destructive",
      });
      return;
    }
    
    handleDeleteBusiness(businessId);
  };


  // Separate query for status counts
  const { data: statusCounts } = useQuery({
    queryKey: ['admin', 'businesses-counts', currentCommunity.id],
    queryFn: async () => {
      const [allCount, pendingCount, pendingSubCount, listedCount] = await Promise.all([
        supabase.from('businesses').select('*', { count: 'exact', head: true }).eq('community_id', currentCommunity.id),
        supabase.from('businesses').select('*', { count: 'exact', head: true }).eq('community_id', currentCommunity.id).eq('is_active', false),
        supabase.from('businesses').select('*', { count: 'exact', head: true }).eq('community_id', currentCommunity.id).eq('is_active', true).eq('is_listed', false),
        supabase.from('businesses').select('*', { count: 'exact', head: true }).eq('community_id', currentCommunity.id).eq('is_listed', true)
      ]);

      return {
        all: allCount.count || 0,
        pending: pendingCount.count || 0,
        "pending-subscription": pendingSubCount.count || 0,
        listed: listedCount.count || 0
      };
    },
    enabled: !!user && !!currentCommunity.id
  });

  const businesses = businessesResponse?.businesses || [];
  const totalCount = businessesResponse?.totalCount || 0;
  const totalPages = businessesResponse?.totalPages || 0;

  const getStatusBadge = (business) => {
    const status = getBusinessStatus(business.is_active, business.is_listed);
    return <Badge variant="outline" className={status.className}>{status.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }).format(date);
  };

  // Component to render delete button with subscription check
  const DeleteBusinessButton = ({ business }: { business: Business }) => {
    // Check if business has active subscription based on the subscription data
    const hasActiveSubscription = business.subscription ? (() => {
      const { status, current_period_end } = business.subscription;
      const now = new Date();
      const periodEnd = new Date(current_period_end);
      
      return (
        ['active', 'trialing', 'past_due'].includes(status) ||
        (periodEnd > now && !['unpaid', 'incomplete_expired'].includes(status))
      );
    })() : false;

    return (
      <Button 
        variant="outline" 
        size="icon" 
        className={`h-8 w-8 ${hasActiveSubscription 
          ? 'text-muted-foreground hover:bg-muted/50 cursor-not-allowed opacity-50' 
          : 'text-destructive hover:bg-destructive/10'
        }`}
        disabled={hasActiveSubscription}
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteBusinessWithCheck(business.id, hasActiveSubscription);
        }}
        title={hasActiveSubscription 
          ? "Cannot delete business with active subscription" 
          : "Delete business"
        }
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    );
  };


  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div className="relative">
        <h1 className="admin-section-title mb-2">Business Management</h1>
        <p className="text-muted-foreground/80 text-lg">
          Manage all the business listings across the platform.
        </p>
      </div>

      <Card className="admin-card admin-card-stats">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 pb-6">
          <div>
            <CardTitle className="text-xl font-bold text-slate-800">All Businesses</CardTitle>
            <CardDescription className="text-muted-foreground/80 mt-2">
              View and manage all business listings across your community
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search businesses..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="mb-4">
            <TabsList className="w-full md:w-auto grid grid-cols-2 md:flex md:max-w-full h-auto md:h-10 gap-1 md:gap-0 p-1">
              <TabsTrigger value="all" className="w-full md:flex-1 text-xs md:text-sm px-1 py-3 md:px-3 md:py-2 text-center leading-tight">
                All ({statusCounts?.all || 0})
              </TabsTrigger>
              <TabsTrigger value="pending" className="w-full md:flex-1 text-xs md:text-sm px-1 py-3 md:px-3 md:py-2 text-center leading-tight">
                Pending Validation ({statusCounts?.pending || 0})
              </TabsTrigger>
              <TabsTrigger value="pending-subscription" className="w-full md:flex-1 text-xs md:text-sm px-1 py-3 md:px-3 md:py-2 text-center leading-tight">
                Pending Subscription ({statusCounts?.["pending-subscription"] || 0})
              </TabsTrigger>
              <TabsTrigger value="listed" className="w-full md:flex-1 text-xs md:text-sm px-1 py-3 md:px-3 md:py-2 text-center leading-tight">
                Listed ({statusCounts?.listed || 0})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="flex space-x-2">
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
              <p>Error loading businesses</p>
            </div>
          ) : (
            <>
              <div className="block md:hidden space-y-4">
                {businesses && businesses.length > 0 ? (
                  businesses.map((business, index) => (
                    <Card 
                      key={business.id} 
                      className={`admin-card admin-card-action animate-fade-in stagger-${Math.min(index + 1, 5)} `}
                      onClick={() => handleEditBusiness(business.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="text-base font-medium">{business.name}</h3>
                            <p className="text-xs text-muted-foreground mt-1">
                              {getCategoryName(business.category_id)}
                              {business.city && <span> · {business.city}</span>}
                            </p>
                          </div>
                          <div className="flex flex-col gap-1">
                            {getStatusBadge(business)}
                            <BusinessSubscriptionBadgeStatic subscription={business.subscription} className="text-xs" />
                          </div>
                        </div>
                        
                        <div className="text-xs text-muted-foreground flex items-center mt-2">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(business.created_at)}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t flex justify-end gap-2 select-none" onClick={(e) => e.stopPropagation()}>
                          {!business.is_active && (
                            <Button 
                              variant="outline" 
                              size="icon"
                              className="h-8 w-8"
                              data-testid="review-business-mobile-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReviewBusiness(business.id);
                              }}
                            >
                              <ClipboardCheck className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditBusiness(business.id);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <DeleteBusinessButton business={business} />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-8">
                    {searchTerm ? (
                      <p className="text-muted-foreground">No businesses match your search.</p>
                    ) : (
                      <p className="text-muted-foreground">No businesses have been added yet.</p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="hidden md:block rounded-xl border border-gray-200/60 overflow-hidden bg-white shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-gray-100 bg-gray-50/50">
                      <TableHead className="font-semibold text-slate-700 h-12">Business Name</TableHead>
                      <TableHead className="font-semibold text-slate-700">Category</TableHead>
                      <TableHead className="font-semibold text-slate-700">Location</TableHead>
                      <TableHead className="font-semibold text-slate-700">Contact</TableHead>
                      <TableHead className="font-semibold text-slate-700">Status</TableHead>
                      <TableHead className="font-semibold text-slate-700">Subscription</TableHead>
                      <TableHead className="text-right font-semibold text-slate-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {businesses && businesses.length > 0 ? (
                      businesses.map((business) => (
                        <TableRow 
                          key={business.id} 
                          className="cursor-pointer hover:bg-gray-50/80 border-b border-gray-100/50 transition-colors"
                          onClick={() => handleEditBusiness(business.id)}
                        >
                          <TableCell className="font-medium">{business.name}</TableCell>
                          <TableCell>{getCategoryName(business.category_id)}</TableCell>
                          <TableCell>{business.city || 'N/A'}</TableCell>
                          <TableCell>
                            <div className="flex flex-col text-sm">
                              <span>{business.contact_email}</span>
                              <span>{business.contact_phone}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(business)}
                          </TableCell>
                          <TableCell>
                            <BusinessSubscriptionBadgeStatic subscription={business.subscription} />
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end gap-2">
                              {!business.is_active && (
                                <Button 
                                  variant="outline" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  data-testid="review-business-desktop-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReviewBusiness(business.id);
                                  }}
                                >
                                  <ClipboardCheck className="h-4 w-4" />
                                </Button>
                              )}
                              <Button 
                                variant="outline" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditBusiness(business.id);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <DeleteBusinessButton business={business} />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          {searchTerm ? (
                            <div>
                              <p className="text-muted-foreground">No businesses match your search.</p>
                            </div>
                          ) : (
                            <div>
                              <p className="text-muted-foreground">No businesses have been added yet.</p>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          <AdminPagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </CardContent>
      </Card>
      
      <BusinessReviewSheet 
        businessId={reviewBusinessId} 
        isOpen={!!reviewBusinessId}
        onClose={() => setReviewBusinessId(null)}
      />

      <BusinessEditSheet 
        businessId={editBusinessId} 
        isOpen={!!editBusinessId}
        onClose={() => setEditBusinessId(null)}
      />

    </div>
  );
};

export default BusinessManagement;
